// Tokenizer for `${...}` loop placeholder expressions.
// Example: `bp.size + '-' + state` becomes identifier/operator tokens.
import {
  buildSingleCharToken,
  isDigit,
  isIdentifierStart,
  isWhitespace,
  readIdentifierToken,
  readNumberToken,
  readStringToken,
} from './placeholder-expression-token-readers'
import { readVarCallToken } from './placeholder-expression-var'
import type { LoopExpressionToken } from './placeholder-expression-types'
// Check whitespace token flow.
// Check legacy token flow.
// Check operator token flow.
// Check quoted token flow.
// Check var-call token flow.
// Check identifier token flow.

// Tokenize one `${...}` expression into parser-ready tokens.
export function tokenizeLoopExpression(expression: string): LoopExpressionToken[] | null {
  const tokens: LoopExpressionToken[] = []
  let index = 0

  // Scan the expression left-to-right and emit parser tokens.
  while (index < expression.length) {
    const char = expression[index]
    const next = expression[index + 1] ?? ''

    // Skip whitespace between expression tokens.
    if (isWhitespace(char)) {
      index += 1
      continue
    }

    // Reject legacy `$name` usage inside `${...}` expressions.
    if (char === '$') {
      throw new Error(
        'Invalid .luis syntax: nested or legacy `$...` usage inside `${...}` is not supported. Use plain identifiers like `${i + j}`.',
      )
    }

    // Emit single-char operator and parenthesis tokens.
    if (char === '+' || char === '-' || char === '*' || char === '/' || char === '(' || char === ')') {
      tokens.push(buildSingleCharToken(char))
      index += 1
      continue
    }

    // Parse quoted string literals as one token.
    if (char === "'" || char === '"') {
      const parsedString = readStringToken(expression, index, char)
      if (!parsedString) {
        // Return null for malformed quoted string expressions.
        return null
      }
      tokens.push({ kind: 'string', value: parsedString.value })
      index = parsedString.end
      continue
    }

    // Parse numeric literals with optional unit suffixes.
    if (isDigit(char) || (char === '.' && isDigit(next))) {
      const parsedNumber = readNumberToken(expression, index)
      tokens.push({ kind: 'number', value: parsedNumber.value })
      index = parsedNumber.end
      continue
    }

    // Parse compile-time `var(...)` calls before generic identifiers.
    if (char === 'v') {
      const parsedVarCall = readVarCallToken(expression, index)
      if (parsedVarCall) {
        tokens.push({ kind: 'var-call', value: parsedVarCall.value })
        index = parsedVarCall.end
        continue
      } else {
        // Continue identifier parsing when no supported var-call exists.
      }
    } else {
      // Continue identifier parsing for non-`v` token starts.
    }

    // Parse dotted identifier references like `bp.size`.
    if (isIdentifierStart(char)) {
      const parsedIdentifier = readIdentifierToken(expression, index)
      tokens.push({ kind: 'identifier', value: parsedIdentifier.value })
      index = parsedIdentifier.end
      continue
    }

    // Return null for unsupported expression characters.
    return null
  }

  // Return the full token list for parser consumption.
  return tokens
}
